import type { OrderLogsFilter } from '@/api/hooks/useIntegrationLogs'
import type { components } from '@/types/api/v1'
import {
  ISSUE_CATEGORY_LABELS,
  V3_LOG_CATEGORIES,
} from './LogDetailsPanel/categories'
import { checkpointLabel } from './orderCheckpoints'

export type CheckpointStatus = components['schemas']['CheckpointStatus']

/** A value the endpoint's `checkpoint_states` accepts. `null` matches a
 * checkpoint that hasn't happened -- stored as null on the log group, so
 * Mongo's `$in: [null]` is what finds it. */
export type CheckpointState = CheckpointStatus | null

/**
 * What the Filters popover can narrow the log overview by, in the popover's
 * own terms. `toOrderLogsFilter` turns this into what the endpoint takes.
 */
export interface LogFilters {
  /** Tenant ids, matched against a log's resolved `issue_owners`. */
  issueOwners: string[]
  /** Log detail `category` values (mapping_v3, request_v3, ...). */
  issueTypes: string[]
  /** CheckpointStatus values; null is a checkpoint that never happened. */
  states: CheckpointState[]
  /** Checkpoint field names on OrderIntegrationCheckpoints. */
  events: string[]
}

export const EMPTY_LOG_FILTERS: LogFilters = {
  issueOwners: [],
  issueTypes: [],
  states: [],
  events: [],
}

/** How many filters the popover has set. Drives the Filters button's active
 * state; mirrors activeFilterCount in ordersFilterModel. */
export function activeLogFilterCount(filters: LogFilters): number {
  return (
    filters.issueOwners.length +
    filters.issueTypes.length +
    filters.states.length +
    filters.events.length
  )
}

export interface OwnerOption {
  value: string
  label: string
}

export const ISSUE_TYPE_OPTIONS = V3_LOG_CATEGORIES.filter(
  (category) => category in ISSUE_CATEGORY_LABELS,
).map((category) => ({
  value: category,
  label: ISSUE_CATEGORY_LABELS[category],
}))

/** `id` is what goes into `checkpoint_states`. */
export const STATE_OPTIONS: {
  id: CheckpointState
  label: string
  color: string
}[] = [
  { id: 'failed', label: 'Failure', color: 'var(--theme-error)' },
  { id: 'warning', label: 'Warning', color: 'var(--theme-warning)' },
  { id: 'succeeded', label: 'Success', color: 'var(--theme-success)' },
  { id: null, label: 'None', color: 'var(--gray-400)' },
]

/**
 * What a checkpoints-only selection widens to. Deliberately excludes null:
 * checking just "Order Creation" means "rows where that happened", and
 * `$in: [null]` also matches rows where the checkpoint is absent, which would
 * turn the selection into a no-op.
 */
const CONCRETE_STATES = STATE_OPTIONS.flatMap((option) =>
  option.id ? [option.id] : [],
)

/**
 * Checkpoint field names on OrderIntegrationCheckpoints -- that's what the
 * endpoint's `checkpoints` filter matches on -- with the severity each
 * carries when it goes wrong (display only). Labels come from
 * checkpointLabel, so this list reads like the checkpoint dots and the grids'
 * Event column. The order is product's, and differs from the dots' drawing
 * order deliberately: this list is grouped by severity.
 */
export const EVENT_OPTIONS: {
  id: string
  label: string
  severity: string
}[] = [
  { id: 'created', severity: 'high' },
  { id: 'structure_updates', severity: 'high' },
  { id: 'bols_saved', severity: 'high' },
  { id: 'drops_saved', severity: 'high' },
  { id: 'invoices_sent', severity: 'high' },
  { id: 'canceled', severity: 'medium' },
  { id: 'reset', severity: 'medium' },
  { id: 'bol_images_saved', severity: 'low' },
  { id: 'completed', severity: 'low' },
  { id: 'status_updates', severity: 'low' },
].map(({ id, severity }) => ({ id, severity, label: checkpointLabel(id) }))

const ALL_CHECKPOINTS = EVENT_OPTIONS.map((option) => option.id)

/**
 * Null when nothing is selected, so the query stays exactly as it was before
 * the popover existed.
 *
 * The endpoint honors `checkpoints` and `checkpoint_states` only together --
 * half a checkpoint filter is deliberately a no-op server-side (see
 * OrderLogsQuery.to_query) -- so a one-sided selection is widened to "any" on
 * the other side, which is what checking only "Failure" (or only "Order
 * Creation") plainly means.
 */
export function toOrderLogsFilter(filters: LogFilters): OrderLogsFilter | null {
  const checkpointFilter =
    filters.states.length > 0 || filters.events.length > 0
  if (
    !filters.issueOwners.length &&
    !filters.issueTypes.length &&
    !checkpointFilter
  ) {
    return null
  }
  return {
    issue_owner: filters.issueOwners,
    issue_types: filters.issueTypes,
    checkpoint_states: checkpointFilter
      ? filters.states.length
        ? filters.states
        : CONCRETE_STATES
      : [],
    checkpoints: checkpointFilter
      ? filters.events.length
        ? filters.events
        : ALL_CHECKPOINTS
      : [],
  }
}
